import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Drawer,
  DrawerHeader,
  DrawerPopup,
  DrawerTitle,
  DrawerTrigger,
  Icon,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  Popover,
  PopoverPopup,
  PopoverTrigger,
  ScrollArea,
  Select,
  SelectItem,
  SelectPopup,
  SelectTrigger,
  SelectValue,
  Separator,
  Spinner,
  Tabs,
  TabsList,
  TabsPanel,
  TabsTab,
  Text,
  useIsMobile,
} from "@salestrak/ui";

import { useMemberAccess } from "#app/lib/hooks/use-member-access";
import type { ObjectAccessLevel } from "#app/lib/data";
import type { Omittable } from "@salestrak/ui";

// Per-user access panel — the popover body behind the settings members
// table's Access cell. Data-inverse of the share dialogs: instead of
// "who can see this object", it lists the workflows / items THIS user
// has explicit access to. Workflows additionally show the rest of
// the workspace's workflows as add candidates; items only list what's
// already granted (the issue universe is unbounded).

const ACCESS_LEVEL_OPTIONS: ReadonlyArray<{
  value: ObjectAccessLevel;
  label: string;
}> = [
  { value: "full_edit", label: "Full Edit" },
  { value: "edit", label: "Edit" },
  { value: "comment", label: "Comment" },
  { value: "view", label: "View" },
];
const ACCESS_LEVEL_LABEL: Record<ObjectAccessLevel, string> = {
  full_edit: "Full Edit",
  edit: "Edit",
  comment: "Comment",
  view: "View",
};

// Count button rendered in the members table's Access cell — each one
// anchors its own surface and opens the panel on its tab. Mobile branches to
// a Drawer, which needs an accessible name and so authors its own title.
export function MemberAccessCountButton({
  userId,
  tab,
  label,
}: {
  userId: string;
  tab: "workflows" | "items";
  label: string;
}) {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);

  const trigger = (
    <Box
      as="button"
      type="button"
      paddingX="xs"
      paddingY="2xs"
      rounded="sm"
      hoverBackground
      cursor="pointer"
    >
      <Text size="xs" variant="muted">
        {label}
      </Text>
    </Box>
  );

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={setOpen}>
        <DrawerTrigger render={trigger} />
        <DrawerPopup>
          <DrawerHeader>
            <DrawerTitle>Access</DrawerTitle>
          </DrawerHeader>
          <Box padding="sm">
            <MemberAccessPanel userId={userId} initialTab={tab} />
          </Box>
        </DrawerPopup>
      </Drawer>
    );
  }

  return (
    <Popover>
      <PopoverTrigger render={trigger} />
      <PopoverPopup align="start" className="w-[520px] p-2">
        <MemberAccessPanel userId={userId} initialTab={tab} />
      </PopoverPopup>
    </Popover>
  );
}

export function MemberAccessPanel({
  userId,
  initialTab = "workflows",
}: Omittable<{
  userId: string;
  // Which tab opens first — the Access cell's two count buttons jump
  // straight to their own tab.
  initialTab?: "workflows" | "items";
}>) {
  const {
    loading,
    accessList,
    setWorkflowAccess,
    setItemAccess,
    removeAllWorkflows,
    removeAllItems,
  } = useMemberAccess(userId);

  const [activeTab, setActiveTab] = useState<string>(initialTab);
  const [filter, setFilter] = useState("");
  const q = filter.trim().toLowerCase();

  const grantedWorkflows = useMemo(
    () =>
      (accessList?.workflows ?? []).filter(
        (w) => !q || w.name.toLowerCase().includes(q),
      ),
    [accessList, q],
  );
  const availableWorkflows = useMemo(
    () =>
      (accessList?.availableWorkflows ?? []).filter(
        (w) => !q || w.name.toLowerCase().includes(q),
      ),
    [accessList, q],
  );
  const grantedItems = useMemo(
    () =>
      (accessList?.items ?? []).filter(
        (i) => !q || i.name.toLowerCase().includes(q),
      ),
    [accessList, q],
  );

  const workflowCount = accessList?.workflows.length ?? 0;
  const itemCount = accessList?.items.length ?? 0;

  if (loading && !accessList) {
    return (
      <Box align="center" justify="center" padding="md">
        <Spinner />
      </Box>
    );
  }

  return (
    <Box gap="sm">
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(String(v))}>
        <TabsList variant="underline">
          <TabsTab value="workflows">
            <Text size="sm" weight="semibold">
              Workflows ({workflowCount})
            </Text>
          </TabsTab>
          <TabsTab value="items">
            <Text size="sm" weight="semibold">
              Items ({itemCount})
            </Text>
          </TabsTab>
        </TabsList>

        <TabsPanel value="workflows">
          <Box gap="sm">
            <Box direction="row" align="center" gap="sm">
              <Box flex={1}>
                <InputGroup>
                  <InputGroupAddon>
                    <Icon name="search" />
                  </InputGroupAddon>
                  <InputGroupInput
                    type="search"
                    placeholder="Filter workflows..."
                    value={filter}
                    onValueChange={setFilter}
                  />
                </InputGroup>
              </Box>
              <Box
                as="button"
                type="button"
                onClick={removeAllWorkflows}
                paddingX="sm"
                paddingY="2xs"
                rounded="sm"
                hoverBackground
                cursor="pointer"
              >
                <Text size="sm">
                  Remove all
                </Text>
              </Box>
            </Box>

            {/* The scrollbar is user-visible chrome here, so ScrollArea is
                the right fit. */}
            <Box direction="column" maxHeight={360}>
              <ScrollArea>
                <Box gap="2xs" paddingX="xs">
                  {grantedWorkflows.length === 0 ? (
                    <Box paddingY="xs">
                      <Text size="sm" variant="muted">
                        {q ? "No matching workflows." : "No workflow access yet."}
                      </Text>
                    </Box>
                  ) : (
                    grantedWorkflows.map((wf, i) => (
                      <Box
                        key={`${wf.workflowId}-${i}`}
                        direction="row"
                        align="center"
                        gap="sm"
                        paddingY="2xs"
                      >
                        <Box flex={1} direction="row" align="center" gap="xs">
                          <Text size="sm" truncate={1}>
                            {wf.name}
                          </Text>
                          {wf.viaTeam ? (
                            <Text size="xs" variant="muted" truncate={1}>
                              via {wf.teamName ?? "team"}
                            </Text>
                          ) : null}
                        </Box>
                        <Select
                          value={wf.accessLevel}
                          onValueChange={(next: string | null) => {
                            if (!next) return;
                            setWorkflowAccess(
                              wf.workflowId,
                              next as ObjectAccessLevel,
                              true,
                            );
                          }}
                          disabled={wf.viaTeam}
                        >
                          <SelectTrigger size="sm">
                            <SelectValue>
                              {(value: string) =>
                                ACCESS_LEVEL_LABEL[value as ObjectAccessLevel]
                              }
                            </SelectValue>
                          </SelectTrigger>
                          <SelectPopup>
                            {ACCESS_LEVEL_OPTIONS.map((opt) => (
                              <SelectItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </SelectItem>
                            ))}
                          </SelectPopup>
                        </Select>
                        <Button
                          variant="ghost"
                          size="icon-xs"
                          disabled={wf.viaTeam}
                          aria-label={`Remove access to ${wf.name}`}
                          onClick={() =>
                            setWorkflowAccess(
                              wf.workflowId,
                              wf.accessLevel,
                              false,
                            )
                          }
                        >
                          <Icon name="x" size="sm" color="muted" />
                        </Button>
                      </Box>
                    ))
                  )}

                  {availableWorkflows.length > 0 ? (
                    <>
                      <Box paddingY="xs">
                        <Separator />
                      </Box>
                      <Text size="xs" variant="muted" weight="medium">
                        Add to workflow
                      </Text>
                      {availableWorkflows.map((wf, i) => (
                        <Box
                          key={`${wf.workflowId}-${i}`}
                          direction="row"
                          align="center"
                          gap="sm"
                          paddingY="2xs"
                        >
                          <Box flex={1}>
                            <Text size="sm" truncate={1}>
                              {wf.name}
                            </Text>
                          </Box>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              setWorkflowAccess(
                                wf.workflowId,
                                wf.accessLevel,
                                true,
                              )
                            }
                          >
                            <Icon name="plus" size="sm" color="muted" />
                            <Text size="sm" variant="muted">
                              Add
                            </Text>
                          </Button>
                        </Box>
                      ))}
                    </>
                  ) : null}
                </Box>
              </ScrollArea>
            </Box>
          </Box>
        </TabsPanel>

        <TabsPanel value="items">
          <Box gap="sm">
            <Box direction="row" align="center" gap="sm">
              <Box flex={1}>
                <InputGroup>
                  <InputGroupAddon>
                    <Icon name="search" />
                  </InputGroupAddon>
                  <InputGroupInput
                    type="search"
                    placeholder="Filter items..."
                    value={filter}
                    onValueChange={setFilter}
                  />
                </InputGroup>
              </Box>
              <Box
                as="button"
                type="button"
                onClick={removeAllItems}
                paddingX="sm"
                paddingY="2xs"
                rounded="sm"
                hoverBackground
                cursor="pointer"
              >
                <Text size="sm">
                  Remove all
                </Text>
              </Box>
            </Box>

            <Box direction="column" maxHeight={360}>
              <ScrollArea>
                <Box gap="2xs" paddingX="xs">
                  {grantedItems.length === 0 ? (
                    <Box paddingY="xs">
                      <Text size="sm" variant="muted">
                        {q ? "No matching items." : "No item access yet."}
                      </Text>
                    </Box>
                  ) : (
                    grantedItems.map((item, i) => (
                      <Box
                        key={`${item.issueId}-${i}`}
                        direction="row"
                        align="center"
                        gap="sm"
                        paddingY="2xs"
                      >
                        <Box flex={1} direction="row" align="center" gap="xs">
                          <Text size="sm" truncate={1}>
                            {item.name}
                          </Text>
                          {item.viaTeam ? (
                            <Text size="xs" variant="muted" truncate={1}>
                              via {item.teamName ?? "team"}
                            </Text>
                          ) : null}
                        </Box>
                        <Select
                          value={item.accessLevel}
                          onValueChange={(next: string | null) => {
                            if (!next) return;
                            setItemAccess(
                              item.issueId,
                              next as ObjectAccessLevel,
                              true,
                            );
                          }}
                          disabled={item.viaTeam}
                        >
                          <SelectTrigger size="sm">
                            <SelectValue>
                              {(value: string) =>
                                ACCESS_LEVEL_LABEL[value as ObjectAccessLevel]
                              }
                            </SelectValue>
                          </SelectTrigger>
                          <SelectPopup>
                            {ACCESS_LEVEL_OPTIONS.map((opt) => (
                              <SelectItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </SelectItem>
                            ))}
                          </SelectPopup>
                        </Select>
                        <Button
                          variant="ghost"
                          size="icon-xs"
                          disabled={item.viaTeam}
                          aria-label={`Remove access to ${item.name}`}
                          onClick={() =>
                            setItemAccess(
                              item.issueId,
                              item.accessLevel,
                              false,
                            )
                          }
                        >
                          <Icon name="x" size="sm" color="muted" />
                        </Button>
                      </Box>
                    ))
                  )}
                </Box>
              </ScrollArea>
            </Box>
          </Box>
        </TabsPanel>
      </Tabs>
    </Box>
  );
}
